import {isLoaded, PackageModel, UnassignedPackageModel} from 'ApplicationEntry/models/ProductsRiders/PackageModel';
import {Loaded} from 'Core/models/Async';
import {EmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

type AIPProps = {
  units: StringUnion;
  planType: StringUnion;
  indWeeklyPremium: StringUnion;
  familyWeeklyPremium: StringUnion;
  indMonthlyPremium: StringUnion;
  familyMonthlyPremium: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  section125: boolean;
  packageOption: StringUnion;
  situs: StringUnion;
  jstamp: boolean;
  packageList: PackageModel;
};

export class InvalidGroupAIP {
  public readonly key = 'InvalidGroupAIP';

  constructor(
    public readonly units: StringUnion,
    public readonly planType: StringUnion,
    public readonly indWeeklyPremium: StringUnion,
    public readonly familyWeeklyPremium: StringUnion,
    public readonly indMonthlyPremium: StringUnion,
    public readonly familyMonthlyPremium: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly packageOption: StringUnion,
    public readonly situs: StringUnion,
    public readonly jstamp: boolean,
    public readonly packageList: PackageModel
  ) {}
}

export class ValidGroupAIP {
  public static createApp({
    units,
    planType,
    indWeeklyPremium,
    familyWeeklyPremium,
    indMonthlyPremium,
    familyMonthlyPremium,
    premium,
    calculatedPremium,
    cwa,
    section125,
    packageOption,
    situs,
    jstamp,
    packageList,
  }: AIPProps) {
    return this.create(
      units,
      planType,
      indWeeklyPremium,
      familyWeeklyPremium,
      indMonthlyPremium,
      familyMonthlyPremium,
      premium,
      calculatedPremium,
      cwa,
      section125,
      packageOption,
      situs,
      jstamp,
      packageList
    );
  }

  public static create(
    units: StringUnion,
    planType: StringUnion,
    indWeeklyPremium: StringUnion,
    familyWeeklyPremium: StringUnion,
    indMonthlyPremium: StringUnion,
    familyMonthlyPremium: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    section125: boolean,
    packageOption: StringUnion,
    situs: StringUnion,
    jstamp: boolean,
    packageList: PackageModel
  ) {
    return isLoaded(packageList)
      ? new ValidGroupAIP(
          units,
          planType,
          indWeeklyPremium,
          familyWeeklyPremium,
          indMonthlyPremium,
          familyMonthlyPremium,
          premium,
          calculatedPremium,
          cwa,
          section125,
          packageOption,
          situs,
          jstamp,
          packageList
        )
      : new InvalidGroupAIP(
          units,
          planType,
          indWeeklyPremium,
          familyWeeklyPremium,
          indMonthlyPremium,
          familyMonthlyPremium,
          premium,
          calculatedPremium,
          cwa,
          section125,
          packageOption,
          situs,
          jstamp,
          packageList
        );
  }

  public readonly key = 'ValidGroupAIP';
  constructor(
    public readonly units: StringUnion,
    public readonly planType: StringUnion,
    public readonly indWeeklyPremium: StringUnion,
    public readonly familyWeeklyPremium: StringUnion,
    public readonly indMonthlyPremium: StringUnion,
    public readonly familyMonthlyPremium: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly packageOption: StringUnion,
    public readonly situs: StringUnion,
    public readonly jstamp: boolean,
    public readonly packageList: Loaded<object[]>
  ) {}
}

export type GroupAIP = InvalidGroupAIP | ValidGroupAIP;

export type GroupAIPOptions<TReturn> = ToPartialMatchOptions<GroupAIP, 'key', TReturn>;

export const matchGroupAIP = <TReturn>(options: GroupAIPOptions<TReturn>) => (app: GroupAIP): TReturn => {
  switch (app.key) {
    case 'InvalidGroupAIP':
      return options.InvalidGroupAIP ? options.InvalidGroupAIP(app) : options.default(app);
    case 'ValidGroupAIP':
      return options.ValidGroupAIP ? options.ValidGroupAIP(app) : options.default(app);
  }
};

export const EmptyGroupAIP = new InvalidGroupAIP(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  new EmptyString(),
  new EmptyString(),
  false,
  new UnassignedPackageModel()
);

export const changeUnits = (units: string) => (app: GroupAIP) =>
  ValidGroupAIP.createApp({...app, units: NonEmptyString.create(units)});
export const changePlanType = (planType: string) => (app: GroupAIP) =>
  ValidGroupAIP.createApp({...app, planType: NonEmptyString.create(planType)});
export const changeIndWeeklyPremium = (indWeeklyPremium: string) => (app: GroupAIP) =>
  ValidGroupAIP.createApp({...app, indWeeklyPremium: NonEmptyString.create(indWeeklyPremium)});
export const changeFamilyWeeklyPremium = (familyWeeklyPremium: string) => (app: GroupAIP) =>
  ValidGroupAIP.createApp({...app, familyWeeklyPremium: NonEmptyString.create(familyWeeklyPremium)});
export const changeIndMonthlyPremium = (indMonthlyPremium: string) => (app: GroupAIP) =>
  ValidGroupAIP.createApp({...app, indMonthlyPremium: NonEmptyString.create(indMonthlyPremium)});
export const changeFamilyMonthlyPremium = (familyMonthlyPremium: string) => (app: GroupAIP) =>
  ValidGroupAIP.createApp({...app, familyMonthlyPremium: NonEmptyString.create(familyMonthlyPremium)});
export const changePremium = (premium: string) => (app: GroupAIP) =>
  ValidGroupAIP.createApp({...app, premium: NonEmptyString.create(premium)});
export const changeCalculatedPremium = (calculatedPremium: string) => (app: GroupAIP) =>
  ValidGroupAIP.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});
export const changeCWA = (cwa: string) => (app: GroupAIP) =>
  ValidGroupAIP.createApp({...app, cwa: NonEmptyString.create(cwa)});
export const changeSection = (section: boolean) => (app: GroupAIP) =>
  ValidGroupAIP.createApp({...app, section125: section});
export const changePackage = (packageOption: string) => (app: GroupAIP) =>
  ValidGroupAIP.createApp({...app, packageOption: NonEmptyString.create(packageOption)});
export const changeSitus = (situs: string) => (app: GroupAIP) =>
  ValidGroupAIP.createApp({...app, situs: NonEmptyString.create(situs)});
export const changeJStamp = (jstamp: boolean) => (app: GroupAIP) => ValidGroupAIP.createApp({...app, jstamp});
export const changePackageList = (packages: PackageModel) => (app: GroupAIP) =>
  ValidGroupAIP.createApp({...app, packageList: packages});
